import { Button, Flex, Form, Input, Popover, Switch } from 'antd';
import { useEffect, useMemo, type FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { Text } from '@shared/ui/antd';
import { useFormSchema } from '@modules/channels/hooks/use-form-schema';
import type { ChannelVkFormValues } from '@modules/channels/lib/types';
import { CHANNELS_MAX_FORM_LIMIT } from '@modules/channels/lib/constants';
import { AgentsSelector } from '@shared/ui/selector/agents-selector';
import { useAppDispatch, useAppSelector } from '@shared/redux';
import {
  channelFormDraftsActions,
  channelFormDraftsSelectors,
} from '@shared/redux/ducks/channel-form-drafts';
import styles from '../channel-create-form.module.scss';

interface VkCreateFormProps {
  isSubmitLoading: boolean;
  onSubmit: (values: ChannelVkFormValues) => Promise<void>;
  onClose: () => void;
  preselectedAgentId?: number;
  draftKey: string;
}

export const VkCreateForm: FC<VkCreateFormProps> = ({
  isSubmitLoading,
  onSubmit,
  onClose,
  preselectedAgentId,
  draftKey,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('translation', { keyPrefix: 'channels.form' });
  const { vkSchema } = useFormSchema();
  const savedDraft = useAppSelector(channelFormDraftsSelectors.selectDraftByKey(draftKey)) as
    | Partial<ChannelVkFormValues>
    | undefined;
  const defaultValues = useMemo<ChannelVkFormValues>(
    () => ({
      name: savedDraft?.name ?? '',
      description: savedDraft?.description ?? '',
      token: savedDraft?.token ?? '',
      isActive: savedDraft?.isActive ?? true,
      agentId:
        preselectedAgentId ??
        savedDraft?.agentId ??
        (undefined as unknown as ChannelVkFormValues['agentId']),
    }),
    [preselectedAgentId, savedDraft],
  );
  const tokenHelpContent = (
    <Flex vertical gap={8} style={{ maxWidth: 320 }}>
      <Text variant="sm" fontWeight={600}>
        {t('tokenHelpTitle')}
      </Text>
      <Text variant="xs">{t('tokenHelpVkStep1')}</Text>
      <Text variant="xs">{t('tokenHelpVkStep2')}</Text>
      <Text variant="xs">{t('tokenHelpVkStep3')}</Text>
    </Flex>
  );

  const {
    handleSubmit,
    control,
    watch,
    reset,
    clearErrors,
    formState: { errors, isSubmitting, isValid, isDirty },
  } = useForm<ChannelVkFormValues>({
    mode: 'onTouched',
    resolver: zodResolver(vkSchema),
    defaultValues,
  });

  useEffect(() => {
    const subscription = watch((values) => {
      dispatch(
        channelFormDraftsActions.upsertDraft({
          key: draftKey,
          values: values as Record<string, unknown>,
        }),
      );
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [dispatch, draftKey, watch]);

  const handleSubmitForm = async (values: ChannelVkFormValues) => {
    const submitValues = preselectedAgentId ? { ...values, agentId: preselectedAgentId } : values;
    await onSubmit(submitValues);
    dispatch(channelFormDraftsActions.removeDraft(draftKey));
    reset();
  };

  const handleCancel = () => {
    dispatch(channelFormDraftsActions.removeDraft(draftKey));
    reset();
    onClose();
  };

  return (
    <Form
      onChange={() => clearErrors('root')}
      onFinish={handleSubmit(handleSubmitForm)}
      className={styles.form}
      layout="vertical"
    >
      <Flex vertical className={styles.section}>
        <Flex vertical className={styles.sectionHeader}>
          <Text variant="md" fontWeight={600}>
            {t('tokenTitle')}
          </Text>
          <Text variant="xs" className={styles.sectionDescription}>
            {t('tokenHintVk')}
          </Text>
        </Flex>
        <Form.Item
          name="token"
          help={errors.token?.message}
          validateStatus={errors.token ? 'error' : ''}
        >
          <Controller
            control={control}
            name="token"
            render={({ field }) => (
              <Input
                {...field}
                disabled={isSubmitting}
                placeholder={t('tokenPlaceholder')}
                className={styles.input}
              />
            )}
          />
        </Form.Item>
        <Popover content={tokenHelpContent} trigger="hover" placement="bottomLeft">
          <Text variant="xs" className={styles.helpLink}>
            {t('tokenHelpLinkVk')}
          </Text>
        </Popover>
      </Flex>

      {!preselectedAgentId && (
        <Flex vertical className={styles.section}>
          <Flex vertical className={styles.sectionHeader}>
            <Text variant="md" fontWeight={600}>
              {t('agentTitle')}
            </Text>
            <Text variant="xs" className={styles.sectionDescription}>
              {t('agentHintChannel')}
            </Text>
          </Flex>
          <Form.Item
            name="agentId"
            help={errors.agentId?.message}
            validateStatus={errors.agentId ? 'error' : ''}
          >
            <Controller
              control={control}
              name="agentId"
              rules={{ required: t('common.formErrors.required') as string }}
              render={({ field }) => (
                <AgentsSelector
                  {...field}
                  value={field.value}
                  placeholder={t('agentPlaceholder')}
                  onChange={(value) => field.onChange(value)}
                  className={styles.selector}
                />
              )}
            />
          </Form.Item>
        </Flex>
      )}

      <Flex vertical className={styles.section}>
        <Flex vertical className={styles.sectionHeader}>
          <Text variant="md" fontWeight={600}>
            {t('nameTitle')}
          </Text>
          <Text variant="xs" className={styles.sectionDescription}>
            {t('nameHint')}
          </Text>
        </Flex>
        <Form.Item
          name="name"
          help={errors.name?.message}
          validateStatus={errors.name ? 'error' : ''}
        >
          <Controller
            control={control}
            name="name"
            render={({ field }) => (
              <Input
                {...field}
                showCount
                maxLength={CHANNELS_MAX_FORM_LIMIT.name}
                disabled={isSubmitting}
                placeholder={t('namePlaceholder')}
                className={styles.input}
              />
            )}
          />
        </Form.Item>
      </Flex>

      <Flex vertical className={styles.section}>
        <Flex vertical className={styles.sectionHeader}>
          <Text variant="md" fontWeight={600}>
            {t('description')}
          </Text>
          <Text variant="xs" className={styles.sectionDescription}>
            {t('descriptionHint')}
          </Text>
        </Flex>
        <Form.Item
          name="description"
          help={errors.description?.message}
          validateStatus={errors.description ? 'error' : ''}
        >
          <Controller
            control={control}
            name="description"
            render={({ field }) => (
              <Input
                {...field}
                showCount
                maxLength={CHANNELS_MAX_FORM_LIMIT.description}
                disabled={isSubmitting}
                placeholder={t('descriptionPlaceholder')}
                className={styles.input}
              />
            )}
          />
        </Form.Item>
      </Flex>

      <Flex vertical className={styles.section}>
        <Flex vertical className={styles.sectionHeader}>
          <Text variant="md" fontWeight={600}>
            {t('statusTitle')}
          </Text>
          <Text variant="xs" className={styles.sectionDescription}>
            {t('statusHint')}
          </Text>
        </Flex>
        <Form.Item style={{ marginBottom: 0 }}>
          <Controller
            control={control}
            name="isActive"
            render={({ field }) => (
              <Flex className={styles.switchRow}>
                <Switch
                  checked={field.value}
                  onChange={(checked) => field.onChange(checked)}
                  disabled={isSubmitting}
                />
                <Text variant="sm">
                  {field.value ? t('active') : t('inactive')}
                </Text>
              </Flex>
            )}
          />
        </Form.Item>
      </Flex>

      <Flex className={styles.actions}>
        <Button
          variant="text"
          onClick={handleCancel}
          className={styles.buttonGhost}
          disabled={isSubmitting || isSubmitLoading}
        >
          {t('cancel')}
        </Button>

        <Form.Item className={styles.buttonPrimary}>
          <Button
            className={styles.buttonPrimary}
            type="primary"
            htmlType="submit"
            loading={isSubmitting || isSubmitLoading}
            disabled={!isValid || isSubmitting || isSubmitLoading || !isDirty}
          >
            {t('submitSave')}
          </Button>
        </Form.Item>
      </Flex>
    </Form>
  );
};
